import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import Iconify from "../../components/iconify/Iconify";
import useAuth from "../../hooks/useAuth";
import { ROLES_LIST } from "../roles/roles.config";
import {
  startRolePreview,
  stopRolePreview,
  selectIsPreviewActive,
  selectPreviewRole,
} from "./rolePreviewSlice";

const PREVIEW_ROLES = [
  { value: ROLES_LIST.Mitglied, label: "Mitglied" },
  { value: ROLES_LIST.User, label: "User" },
  { value: ROLES_LIST.Editor, label: "Editor" },
  { value: ROLES_LIST.Admin, label: "Admin" },
];

export default function RolePreviewControl() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isRealAdmin, isRealSuperAdmin } = useAuth();
  const isPreviewActive = useSelector(selectIsPreviewActive);
  const previewRole = useSelector(selectPreviewRole);

  const [selectedRole, setSelectedRole] = useState(ROLES_LIST.Mitglied);

  // Only real Admin / SuperAdmin may use this control
  if (!isRealAdmin && !isRealSuperAdmin) return null;

  const activeRoleLabel = PREVIEW_ROLES.find(
    (r) => r.value === previewRole,
  )?.label;

  const handleStart = () => {
    const targetRole = selectedRole;
    dispatch(
      startRolePreview({
        role: targetRole,
        originPath: location.pathname,
      }),
    );
    // Redirect based on simulated role
    const isMemberOnly = targetRole === ROLES_LIST.Mitglied;
    navigate(isMemberOnly ? "/dashboard/home" : "/dashboard/app");
  };

  const handleStop = () => {
    dispatch(stopRolePreview());
    navigate("/dashboard/app");
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify icon="mdi:eye-outline" width={24} />
            <Typography variant="h6">Rollen-Vorschau</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Simulieren Sie die Ansicht für eine bestimmte Rolle. Die Vorschau
            wirkt sich nur auf die Navigation und Seitensichtbarkeit im Frontend
            aus. Backend-Berechtigungen bleiben unverändert.
          </Typography>

          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            Die Vorschau wird bei einem Seiten-Reload automatisch beendet und
            nicht gespeichert.
          </Alert>

          {isPreviewActive ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Vorschau aktiv als <strong>{activeRoleLabel}</strong>. Einige
                Admin-Bereiche sind in dieser Ansicht nicht sichtbar, da sie für
                die simulierte Rolle nicht verfügbar wären.
              </Alert>
              <Button
                variant="contained"
                color="warning"
                startIcon={<Iconify icon="mdi:eye-off-outline" />}
                onClick={handleStop}
              >
                Vorschau beenden
              </Button>
            </Box>
          ) : (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-end">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="role-preview-select-label">Rolle</InputLabel>
                <Select
                  labelId="role-preview-select-label"
                  value={selectedRole}
                  label="Rolle"
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  {PREVIEW_ROLES.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<Iconify icon="mdi:eye-outline" />}
                onClick={handleStart}
              >
                Vorschau starten
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
